// Гант на холсте (прототип, 2026-09-18; переключатель «Новый Гант» — только администраторам).
//
// Старый Гант — полоса в каждой ячейке таблицы и стрелки SVG по координатам из разметки:
// каждое движение мыши перерисовывало всю таблицу через состояние React, а стрелки во
// время перетаскивания стояли на месте. Здесь правая часть рисуется одним <canvas>:
//  • координаты считаются из данных (день → x, номер строки → y), разметку не спрашиваем;
//  • перетаскивание и наведение живут в ref и перерисовывают только холст (кадр — rAF);
//  • холст закреплён (`position: sticky` у обёртки), поэтому прокрутку двигает браузер,
//    а не наш код, и холст не «дрожит» при быстрой прокрутке;
//  • масштаб — Ctrl+колесо над Гантом или кнопки «−»/«+» (память `plot_gant_px`).
// Таблица слева и строки остаются HTML: в строке под холстом — пустая ячейка нужной ширины,
// холст прозрачен для мыши (`pointer-events: none`), события слушаем на контейнере таблицы.
// Поэтому подсветка строки, Ctrl-выделение и клики по ячейкам работают как раньше.
// Запись правок — те же функции модели, что и у старого Ганта (Plan.tsx: tyanulZadachu,
// tyanulGruppu, sbrositSdvig): сервер и правила не меняются, меняется только отрисовка.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

const D1 = 86400000;
const MES = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const MES_POLN = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август',
  'сентябрь', 'октябрь', 'ноябрь', 'декабрь'];
/** пределы масштаба, пикселей на день */
export const PX_MIN = 0.4, PX_MAX = 48;
/** порог перетаскивания — как у старого Ганта (защита от случайного щелчка, владелец 2026-09-15) */
const POROG = 6;
/** сколько стрелок рисуем за кадр — больше глаз всё равно не разберёт */
const PREDEL_STRELOK = 3000;

/** Полоса одной строки таблицы. Дни — серийные номера (дней от 1970-01-01), включительно. */
export type HPolosa = {
  a: number; b: number;
  vid: 'etap' | 'rab' | 'zad';
  /** состояние задачи по факту: '' — план, act — начата, done — окончена */
  sost: '' | 'act' | 'done';
  /** цвет полосы (владелец 2026-10-07): oshibka — есть замечания (красная), done — СМР окончены
      (зелёная), fiks — закреплена фактом или ручной датой (жёлтая), '' — обычная (серая) */
  cvet?: '' | 'oshibka' | 'fiks' | 'done';
  proc: number | null;
  /** подвинули, ждёт «Пересчитать» — пунктир */
  zhdet: boolean;
  /** нулевой запас до конца проекта; рамка поверх цвета факта */
  krit?: boolean;
  /** подсказка при наведении */
  podpis: string;
};
export type HPara = { ot: number; do: number; vid: string };

type Props = {
  /** контейнер таблицы (прокрутка) */
  box: React.RefObject<HTMLDivElement>;
  /** полосы по строкам таблицы (индекс = номер строки в display модели) */
  polosy: (HPolosa | null)[];
  /** первый и последний день шкалы */
  d0: number; d1: number;
  /** масштаб, пикселей на день */
  px: number;
  onPx: (px: number) => void;
  /** пары связей и где стоит задача: id задачи → номер строки (свёрнутая — строка группы) */
  pary: HPara[]; gde: Map<number, number>;
  /** можно ли тянуть полосы */
  tyanem: boolean;
  raschet: boolean;
  onTyanul: (di: number, kray: boolean, dney: number, a: number, b: number) => void;
  onDvojnoj: (di: number) => void;
  /** связь мышью, как в MS Project (#127, ручное планирование): полосу тянут вверх или вниз на другую —
   *  «окончание этой → начало той»; без пропа вертикальное движение ничего не делает */
  onSvyaz?: (diOt: number, diDo: number) => void;
  /** выбранная строка (открыта карточка задачи): её связи видны, пока карточка открыта —
   *  #99, владелец 2026-09-18: «при нажатии на работу пусть отображаются связи, как при
   *  наведении на полосу» */
  vybrana?: number;
  /** кнопки «Связи / Сегодня / − / +» рисует шапка модели (владелец 2026-10-06: «кнопки диаграммы
      Ганта — слева от кнопки диаграммы Ганта»); холст отдаёт их действия сюда и свою полосу не рисует */
  knopkiVne?: (k: GantKnopki | null) => void;
};
export type GantKnopki = { vseSvyazi: boolean; pereklSvyazi: () => void; kSegodnya: () => void; masshtab: (k: number) => void };

type Geo = { ganttX: number; fixR: number; theadH: number; ghH: number;
             y0: number; di0: number; pitch: number; W: number; H: number };
type Tash = { di: number; kray: boolean; x0: number; a: number; b: number; dney: number; idet: boolean;
               /** тянем связь (#127): начало по вертикали; мышь сейчас и строка-цель под ней */
               y0: number; svyaz?: boolean; mx?: number; my?: number; diDo?: number };
type Tsveta = Record<string, string>;

const dataDnya = (d: number) => new Date(d * D1);
const ddmm = (d: number) => {
  const t = dataDnya(d);
  return String(t.getUTCDate()).padStart(2, '0') + '.' + String(t.getUTCMonth() + 1).padStart(2, '0')
    + '.' + String(t.getUTCFullYear()).slice(2);
};

/** цвета — из токенов оформления (tokens.css), чтобы холст говорил тем же языком */
function prochitatTsveta(): Tsveta {
  const s = getComputedStyle(document.documentElement);
  const v = (k: string, z: string) => s.getPropertyValue(k).trim() || z;
  return {
    accent: v('--plot-accent', '#5472ed'), warn: v('--plot-warn', '#f79009'),
    done: v('--plot-done', '#0ba360'), etap: v('--plot-sum-etap', '#334e68'),
    rab: v('--plot-sum-rab', '#7b93b5'), line: v('--plot-line', '#e3e5e8'),
    soft: v('--plot-line-soft', '#edeef1'), head: v('--plot-code-bg', '#f2f4f7'),
    text: v('--plot-text', '#1b1f24'), text2: v('--plot-text-2', '#475467'),
    muted: v('--plot-muted-2', '#98a2b3'), danger: v('--plot-danger', '#b42318'),
    strong: v('--plot-border-strong', '#9db1c9'), surface: v('--plot-surface', '#ffffff'),
    font: getComputedStyle(document.body).fontFamily || 'system-ui, sans-serif',
  };
}

export function GantHolst(props: Props) {
  const { box, px, onPx } = props;
  const P = useRef(props);
  P.current = props;
  const holst = useRef<HTMLCanvasElement>(null);
  const podskazka = useRef<HTMLDivElement>(null);
  const knopki = useRef<HTMLDivElement>(null);
  const geo = useRef<Geo | null>(null);
  const navod = useRef<{ di: number; kray: boolean } | null>(null);
  const tash = useRef<Tash | null>(null);
  const kadr = useRef(0);
  const zoomYakor = useRef<{ den: number; cx: number } | null>(null);
  const tsveta = useRef<Tsveta | null>(null);
  const shtrih = useRef<CanvasPattern | null>(null);
  // размер холста и все ли связи показывать — их видит и разметка кнопок
  const [razmer, setRazmer] = useState({ gh: 30 });
  const [vseSvyazi, setVseSvyazi] = useState(() => {
    try { return localStorage.getItem('plot_gant_svyazi') === '1'; } catch { return false; }
  });
  const vse = useRef(vseSvyazi);
  vse.current = vseSvyazi;
  const vyb = useRef(props.vybrana);
  vyb.current = props.vybrana;

  // стрелки: пары задач → пары строк, повторы (свёрнутые группы) схлопываем
  const strelki = useMemo(() => {
    const out: { a: number; b: number; vid: string }[] = [];
    const bylo = new Set<string>();
    for (const pr of props.pary) {
      const a = props.gde.get(pr.ot), b = props.gde.get(pr.do);
      if (a == null || b == null || a === b) continue;
      const k = a + ':' + b + ':' + pr.vid;
      if (bylo.has(k)) continue;
      bylo.add(k);
      out.push({ a, b, vid: pr.vid });
    }
    return out;
  }, [props.pary, props.gde]);
  const S = useRef(strelki);
  S.current = strelki;

  const zaprositKadr = () => {
    if (kadr.current) return;
    kadr.current = requestAnimationFrame(() => { kadr.current = 0; risovat(); });
  };

  /** Замер разметки: где начинается столбец Ганта, ширина закреплённых столбцов слева,
   *  высота шапки и шаг строк. Зовётся после каждой отрисовки таблицы (не на кадр). */
  function zamerit() {
    const el = box.current;
    if (!el) return;
    const br = el.getBoundingClientRect();
    const lx = br.left + el.clientLeft, ty = br.top + el.clientTop;
    const thG = el.querySelector('thead tr + tr th.viz-c') as HTMLElement | null;
    // закреплённые столбцы кончаются блоком «Расчёт» (Начало/Окончание/Длит., #99),
    // а не «Работой»: холст начинается за ними, иначе полосы лягут поверх дат
    const thW = (el.querySelector('thead tr + tr th.col-e')
              || el.querySelector('thead th.col-work')) as HTMLElement | null;
    const thead = el.querySelector('thead') as HTMLElement | null;
    const gh = el.querySelector('thead tr.gh') as HTMLElement | null;
    if (!thG || !thead) { geo.current = null; return; }
    const ganttX = thG.getBoundingClientRect().left - lx + el.scrollLeft;
    const fixR = thW ? Math.max(0, thW.getBoundingClientRect().right - lx) : 0;
    const theadH = thead.getBoundingClientRect().height;
    const stroki = el.querySelectorAll('tbody tr[data-di]');
    let y0 = theadH, di0 = 0, pitch = 25;
    if (stroki.length) {
      const pervaya = stroki[0] as HTMLElement, poslednyaya = stroki[stroki.length - 1] as HTMLElement;
      const i1 = Number(pervaya.dataset.di), i2 = Number(poslednyaya.dataset.di);
      const t1 = pervaya.getBoundingClientRect().top - ty + el.scrollTop;
      const t2 = poslednyaya.getBoundingClientRect().top - ty + el.scrollTop;
      y0 = t1; di0 = i1;
      pitch = i2 > i1 ? (t2 - t1) / (i2 - i1) : pervaya.getBoundingClientRect().height;
    }
    const W = Math.max(0, el.clientWidth - fixR), H = el.clientHeight;
    geo.current = { ganttX, fixR, theadH, ghH: gh ? gh.getBoundingClientRect().height : 30,
                    y0, di0, pitch, W, H };
    const c = holst.current;
    if (c) {
      const dpr = window.devicePixelRatio || 1;
      const w = Math.round(W * dpr), h = Math.round(H * dpr);
      if (c.width !== w || c.height !== h) {
        c.width = w; c.height = h;
        c.style.width = W + 'px'; c.style.height = H + 'px';
      }
      c.style.left = fixR + 'px';
    }
    const gH = gh ? gh.getBoundingClientRect().height : 30;
    setRazmer(r => (r.gh === gH ? r : { gh: gH }));
  }

  // ---- пересчёт координат: день ↔ x холста, строка → y холста ----
  // прокрутку читаем один раз на кадр (skr): чтение scrollTop у разметки на каждую
  // стрелку съедало до 6 мс кадра (профиль 2026-09-18)
  const skr = useRef({ sl: 0, st: 0 });
  const zapomnitProkrutku = () => {
    const el = box.current;
    if (el) skr.current = { sl: el.scrollLeft, st: el.scrollTop };
  };
  const xDnya = (g: Geo, d: number) => g.ganttX + (d - P.current.d0) * P.current.px - skr.current.sl - g.fixR;
  const denX = (g: Geo, x: number) => P.current.d0 + (x + skr.current.sl + g.fixR - g.ganttX) / P.current.px;
  const yStroki = (g: Geo, di: number) => g.y0 + (di - g.di0) * g.pitch - skr.current.st;

  /** полоса строки с учётом перетаскивания: дни и координаты */
  function geom(g: Geo, di: number) {
    const p = P.current.polosy[di];
    if (!p) return null;
    let a = p.a, b = p.b;
    const t = tash.current;
    if (t && t.idet && t.di === di) {
      if (t.kray) b = Math.max(a, b + t.dney);
      else { a += t.dney; b += t.dney; }
    }
    const x1 = xDnya(g, a);
    const x2 = Math.max(x1 + 3, xDnya(g, b + 1));
    const h = p.vid === 'etap' ? 11 : p.vid === 'rab' ? 8 : 12;
    return { p, a, b, x1, x2, yc: yStroki(g, di) + g.pitch / 2, h };
  }

  function risovat() {
    const c = holst.current, g = geo.current, el = box.current;
    if (!c || !g || !el) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;                                 // эмулятор без холста (npm run boot)
    zapomnitProkrutku();
    const sl = skr.current.sl;
    if (!tsveta.current) tsveta.current = prochitatTsveta();
    const T = tsveta.current;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, g.W, g.H);
    const { d0, d1, px } = P.current;
    const gx0 = Math.max(0, g.ganttX - sl - g.fixR);
    // правый край: конец шкалы или край окна — правее Ганта таблица кончается
    const gx1 = Math.min(g.W, g.ganttX + (d1 - d0 + 1) * px - sl - g.fixR);
    // кнопки масштаба — у правого края видимой части Ганта
    if (knopki.current) {
      knopki.current.style.left = g.fixR + Math.max(0, gx0) + 'px';
      knopki.current.style.width = Math.max(0, gx1 - gx0) + 'px';
    }
    if (gx0 >= gx1) return;                            // столбец Ганта за краем окна
    const dA = Math.max(d0, Math.floor(denX(g, gx0))), dB = Math.min(d1 + 1, Math.ceil(denX(g, g.W)));

    // ---------- тело: сетка, выходные, «сегодня» ----------
    ctx.save();
    ctx.beginPath(); ctx.rect(gx0, g.theadH, gx1 - gx0, g.H - g.theadH); ctx.clip();
    if (px >= 5) {                                     // выходные — лёгкой заливкой
      ctx.fillStyle = 'rgba(16,24,40,.035)';
      for (let d = dA; d < dB; d++) {
        const wd = dataDnya(d).getUTCDay();
        if (wd === 0 || wd === 6) ctx.fillRect(xDnya(g, d), g.theadH, px, g.H);
      }
    }
    ctx.lineWidth = 1;
    for (let d = dA; d <= dB; d++) {
      const t = dataDnya(d);
      const mes = t.getUTCDate() === 1;
      const ned = t.getUTCDay() === 1;
      // спокойный фон (владелец 2026-10-18: «в Ганте сложно ориентироваться»): границы месяцев и бледные
      // недели, без дневных линий
      if (!mes && !(ned && px >= 2.2)) continue;
      const x = Math.round(xDnya(g, d)) + 0.5;
      ctx.strokeStyle = mes ? T.line : T.soft; ctx.globalAlpha = mes ? 1 : 0.55;
      ctx.beginPath(); ctx.moveTo(x, g.theadH); ctx.lineTo(x, g.H); ctx.stroke();
      ctx.globalAlpha = 1;
    }
    const segodnya = Math.floor(Date.now() / D1);
    if (segodnya >= dA && segodnya <= dB) {
      const x = Math.round(xDnya(g, segodnya + 0.5)) + 0.5;
      ctx.strokeStyle = T.danger; ctx.globalAlpha = 0.3;   // тонкая сплошная, метка — треугольник в шапке
      ctx.beginPath(); ctx.moveTo(x, g.theadH); ctx.lineTo(x, g.H); ctx.stroke();
      ctx.globalAlpha = 1;
    }

    // ---------- полосы видимых строк ----------
    const st = skr.current.st;
    const diA = Math.max(0, Math.floor((st + g.theadH - g.y0) / g.pitch) + g.di0 - 1);
    const diB = Math.min(P.current.polosy.length - 1, Math.ceil((st + g.H - g.y0) / g.pitch) + g.di0 + 1);
    const nav = navod.current, t = tash.current;
    // тянем → наведение → выбранная строка (карточка задачи открыта кликом)
    const aktivnaya = t && t.idet ? t.di : nav ? nav.di : (vyb.current ?? -1);

    // зебра через строку и мягкая полоса выбранной строки (вместо горизонтальных линий; 2026-10-18)
    for (let di = diA; di <= diB; di++) {
      const y = yStroki(g, di);
      if (y + g.pitch < g.theadH || y > g.H) continue;
      if (di === (vyb.current ?? -1)) { ctx.fillStyle = 'rgba(84,114,237,.10)'; ctx.fillRect(gx0, y, gx1 - gx0, g.pitch); }
      else if (di % 2) { ctx.fillStyle = 'rgba(16,24,40,.022)'; ctx.fillRect(gx0, y, gx1 - gx0, g.pitch); }
    }

    // стрелки — под полосами. По умолчанию только у выбранной/наведённой задачи; «Связи» — все, но бледно,
    // а связи выбранной поверх ярко. Очередь бригады — только у выбранной (2026-10-18)
    const strelkiKadra = S.current;
    if (strelkiKadra.length && (vse.current || aktivnaya >= 0)) {
      let n = 0;
      for (const s of strelkiKadra) {
        if (n >= PREDEL_STRELOK) break;
        const svoya = s.a === aktivnaya || s.b === aktivnaya;
        if (!vse.current && !svoya) continue;
        if (s.vid === 'resurs' && !svoya) continue;
        const ya = yStroki(g, s.a) + g.pitch / 2, yb = yStroki(g, s.b) + g.pitch / 2;
        if (Math.max(ya, yb) < g.theadH || Math.min(ya, yb) > g.H) continue;
        const A = geom(g, s.a), B = geom(g, s.b);
        if (!A || !B) continue;
        n++;
        strelka(ctx, T, A.x2, ya, A.h, B.x1, yb, s.vid, svoya, aktivnaya >= 0 && svoya);
      }
    }

    for (let di = diA; di <= diB; di++) {
      const q = geom(g, di);
      if (!q || q.x2 < gx0 - 2 || q.x1 > g.W + 2) continue;
      polosa(ctx, T, q, P.current.raschet);
      if (di === aktivnaya && P.current.tyanem && !P.current.raschet) {
        ctx.strokeStyle = q.p.vid === 'zad' ? T.accent : T.strong;
        ctx.lineWidth = 1.5;
        ctx.beginPath(); kontur(ctx, q.x1 - 1, q.yc - q.h / 2 - 1, q.x2 - q.x1 + 2, q.h + 2, 4); ctx.stroke();
        // ручка правого края — две риски
        ctx.strokeStyle = 'rgba(255,255,255,.9)'; ctx.lineWidth = 1;
        for (const dx of [3.5, 6]) {
          ctx.beginPath(); ctx.moveTo(q.x2 - dx, q.yc - q.h / 2 + 2.5); ctx.lineTo(q.x2 - dx, q.yc + q.h / 2 - 2.5); ctx.stroke();
        }
      }
    }

    // тянем связь (#127): пунктир от конца полосы к мыши, цель обведена
    if (t && t.idet && t.svyaz) {
      const q = geom(g, t.di);
      if (q && t.mx != null && t.my != null) {
        const c = t.diDo != null ? geom(g, t.diDo) : null;
        ctx.strokeStyle = T.done; ctx.lineWidth = 1.5; ctx.setLineDash([4, 3]);
        ctx.beginPath(); ctx.moveTo(q.x2, q.yc); ctx.lineTo(c ? c.x1 : t.mx, c ? c.yc : t.my); ctx.stroke();
        ctx.setLineDash([]);
        if (c) {
          ctx.lineWidth = 2;
          ctx.beginPath(); kontur(ctx, c.x1 - 2, c.yc - c.h / 2 - 2, c.x2 - c.x1 + 4, c.h + 4, 4); ctx.stroke();
          plashka(ctx, T, 'связь: окончание → начало', c.x2 + 8, c.yc);
        }
      }
    }
    // перетаскивание: тень исходного положения и даты у полосы
    else if (t && t.idet) {
      const q = geom(g, t.di);
      if (q) {
        ctx.setLineDash([3, 3]); ctx.strokeStyle = T.muted; ctx.lineWidth = 1;
        const x1 = xDnya(g, t.a), x2 = Math.max(x1 + 3, xDnya(g, t.b + 1));
        ctx.beginPath(); kontur(ctx, x1, q.yc - q.h / 2, x2 - x1, q.h, 4); ctx.stroke();
        ctx.setLineDash([]);
        const xk = t.kray ? q.x2 : q.x1;
        ctx.strokeStyle = T.accent; ctx.globalAlpha = 0.5;
        ctx.beginPath(); ctx.moveTo(Math.round(xk) + 0.5, g.theadH); ctx.lineTo(Math.round(xk) + 0.5, g.H); ctx.stroke();
        ctx.globalAlpha = 1;
        const tekst = ddmm(q.a) + ' — ' + ddmm(q.b) + '  ·  ' + (q.b - q.a + 1) + ' дн.'
          + (t.dney ? '  (' + (t.dney > 0 ? '+' : '') + t.dney + ')' : '');
        plashka(ctx, T, tekst, q.x2 + 8, q.yc);
      }
    }
    ctx.restore();

    // ---------- шапка: шкала поверх шапки таблицы ----------
    ctx.save();
    ctx.beginPath(); ctx.rect(gx0, 0, gx1 - gx0, g.theadH); ctx.clip();
    ctx.fillStyle = T.head; ctx.fillRect(gx0, 0, gx1 - gx0, g.theadH);
    ctx.strokeStyle = T.line; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(gx0, g.theadH - 0.5); ctx.lineTo(gx1, g.theadH - 0.5); ctx.stroke();
    shkala(ctx, T, g, gx0, dA, dB, px);
    if (segodnya >= dA && segodnya <= dB) {
      const x = xDnya(g, segodnya + 0.5);
      ctx.fillStyle = T.danger;
      ctx.beginPath(); ctx.moveTo(x - 4, g.theadH - 5); ctx.lineTo(x + 4, g.theadH - 5); ctx.lineTo(x, g.theadH); ctx.fill();
    }
    ctx.restore();
    // левая граница столбца Ганта
    if (gx0 > 0) {
      ctx.strokeStyle = T.line;
      ctx.beginPath(); ctx.moveTo(gx0 + 0.5, 0); ctx.lineTo(gx0 + 0.5, g.H); ctx.stroke();
    }
  }

  /** шкала в три уровня (#99, владелец 2026-09-18): год — месяц — неделя или день.
      Нижний уровень зависит от масштаба: дни, дальше недели (дата понедельника),
      на мелком масштабе он пустой — подписи всё равно не читались бы. */
  function shkala(ctx: CanvasRenderingContext2D, T: Tsveta, g: Geo, gx0: number, dA: number, dB: number, px: number) {
    const niz = g.theadH;
    const p1 = Math.round(niz / 3), p2 = Math.round((niz * 2) / 3);   // границы трёх полос
    ctx.textBaseline = 'middle';
    const podpis = (s2: string, xa: number, xb: number, y: number, zhirno: boolean) => {
      // подпись прилипает к левому краю видимой части, пока влезает (как липкие заголовки)
      const x = Math.max(xa, gx0) + (zhirno ? 5 : 3);
      ctx.font = (zhirno ? '600 ' : '') + (zhirno ? '11px ' : '10px ') + T.font;
      const w = ctx.measureText(s2).width;
      if (x + w > xb - 2) return;
      ctx.fillText(s2, x, y);
    };
    const mesShir = px * 30.4;
    const linija = (x: number, ya: number, yb: number) => {
      ctx.beginPath(); ctx.moveTo(Math.round(x) + 0.5, ya); ctx.lineTo(Math.round(x) + 0.5, yb); ctx.stroke();
    };
    ctx.strokeStyle = T.line;
    const t0 = dataDnya(dA);
    // разделители полос
    ctx.beginPath(); ctx.moveTo(gx0, p1 - 0.5); ctx.lineTo(g.ganttX + 1e5, p1 - 0.5); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(gx0, p2 - 0.5); ctx.lineTo(g.ganttX + 1e5, p2 - 0.5); ctx.stroke();
    // 1. годы
    ctx.fillStyle = T.text;
    for (let y = t0.getUTCFullYear(); ; y++) {
      const da = Math.round(Date.UTC(y, 0, 1) / D1), db = Math.round(Date.UTC(y + 1, 0, 1) / D1);
      if (da > dB) break;
      const xa = xDnya(g, da), xb = xDnya(g, db);
      linija(xa, 0, niz);                       // граница года — через всю шапку
      podpis(String(y), xa, xb, p1 / 2, true);
    }
    // 2. месяцы
    ctx.fillStyle = T.text2;
    for (let y = t0.getUTCFullYear(), m = t0.getUTCMonth(); ; m++) {
      if (m > 11) { m = 0; y++; }
      const da = Math.round(Date.UTC(y, m, 1) / D1), db = Math.round(Date.UTC(y, m + 1, 1) / D1);
      if (da > dB) break;
      const xa = xDnya(g, da), xb = xDnya(g, db);
      linija(xa, p1, niz);
      if (mesShir >= 60) podpis(MES_POLN[m], xa, xb, (p1 + p2) / 2, false);
      else if (mesShir >= 26) podpis(MES[m], xa, xb, (p1 + p2) / 2, false);
      else if (mesShir >= 12) podpis(MES[m][0], xa, xb, (p1 + p2) / 2, false);
    }
    // 3. недели или дни
    const yN = (p2 + niz) / 2;
    if (px >= 14) {                                   // дни
      for (let d = dA; d <= dB; d++) {
        const xa = xDnya(g, d);
        const wd = dataDnya(d).getUTCDay();
        if (wd === 0 || wd === 6) { ctx.fillStyle = 'rgba(16,24,40,.05)'; ctx.fillRect(xa, p2, px, niz - p2); }
        linija(xa, p2, niz);
        ctx.fillStyle = wd === 0 || wd === 6 ? T.muted : T.text2;
        ctx.font = '11px ' + T.font;
        const s2 = String(dataDnya(d).getUTCDate());
        ctx.fillText(s2, xa + (px - ctx.measureText(s2).width) / 2, yN);
      }
    } else if (px * 7 >= 16) {                        // недели: дата понедельника
      ctx.fillStyle = T.text2;
      for (let d = dA - 7; d <= dB; d++) {
        if (dataDnya(d).getUTCDay() !== 1) continue;
        const xa = xDnya(g, d), xb = xDnya(g, d + 7);
        linija(xa, p2, niz);
        const t = dataDnya(d);
        podpis(px * 7 >= 38 ? String(t.getUTCDate()).padStart(2, '0') + '.' + String(t.getUTCMonth() + 1).padStart(2, '0')
          : String(t.getUTCDate()), xa, xb, yN, false);
      }
    }
  }

  function kontur(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
    const rr = Math.min(r, w / 2, h / 2);
    ctx.moveTo(x + rr, y);
    ctx.arcTo(x + w, y, x + w, y + h, rr);
    ctx.arcTo(x + w, y + h, x, y + h, rr);
    ctx.arcTo(x, y + h, x, y, rr);
    ctx.arcTo(x, y, x + w, y, rr);
    ctx.closePath();
  }

  function polosa(ctx: CanvasRenderingContext2D, T: Tsveta,
    q: { p: HPolosa; x1: number; x2: number; yc: number; h: number }, raschet: boolean) {
    const { p, x1, x2, yc, h } = q;
    const w = x2 - x1, y = yc - h / 2;
    ctx.globalAlpha = raschet ? 0.6 : 1;
    if (p.vid !== 'zad') {
      // суммарная: сплошная планка и «уголки» вниз на концах, как в MS Project
      const tsvet = p.vid === 'etap' ? T.etap : T.rab;
      if (p.zhdet) {
        ctx.setLineDash([3, 2]); ctx.strokeStyle = tsvet; ctx.lineWidth = 1.2;
        ctx.strokeRect(x1 + 0.5, y + 0.5, w - 1, h - 3); ctx.setLineDash([]);
      } else {
        ctx.fillStyle = tsvet;
        ctx.fillRect(x1, y, w, h - 3);
        const u = Math.min(5, w / 2);
        ctx.beginPath();
        ctx.moveTo(x1, y + h - 3); ctx.lineTo(x1 + u, y + h - 3); ctx.lineTo(x1, y + h + 2); ctx.closePath();
        ctx.moveTo(x2, y + h - 3); ctx.lineTo(x2 - u, y + h - 3); ctx.lineTo(x2, y + h + 2); ctx.closePath();
        ctx.fill();
      }
    } else {
      // цвета полос задач (владелец 2026-10-07): красная — замечания, зелёная — окончены,
      // жёлтая — закреплена фактом/ручной датой, серая — обычная
      const tsvet = p.cvet === 'oshibka' ? T.danger : p.cvet === 'done' ? T.done : p.cvet === 'fiks' ? T.warn
        : p.cvet === '' ? T.rab : p.sost === 'done' ? T.done : p.sost === 'act' ? T.warn : T.accent;
      if (p.zhdet) {
        ctx.fillStyle = 'rgba(11,163,96,.14)';
        ctx.beginPath(); kontur(ctx, x1, y, w, h, 4); ctx.fill();
        ctx.setLineDash([3, 2]); ctx.strokeStyle = T.accent; ctx.lineWidth = 1;
        ctx.beginPath(); kontur(ctx, x1 + 0.5, y + 0.5, w - 1, h - 1, 4); ctx.stroke(); ctx.setLineDash([]);
      } else if (p.sost === 'act' && p.proc != null && p.proc > 0 && p.proc < 100) {
        // начата: светлая полоса, готовность — сплошной частью
        ctx.fillStyle = tsvet; ctx.globalAlpha *= 0.4;
        ctx.beginPath(); kontur(ctx, x1, y, w, h, 4); ctx.fill();
        ctx.globalAlpha = raschet ? 0.6 : 1;
        ctx.save(); ctx.beginPath(); kontur(ctx, x1, y, w, h, 4); ctx.clip();
        ctx.fillRect(x1, y, w * p.proc / 100, h); ctx.restore();
      } else {
        ctx.fillStyle = tsvet;
        if (!p.sost) ctx.globalAlpha *= 0.85;
        ctx.beginPath(); kontur(ctx, x1, y, w, h, 4); ctx.fill();
      }
    }
    if (raschet) {                                     // «Расчётный» — штриховка, как у старого Ганта
      if (!shtrih.current) {
        const s = document.createElement('canvas'); s.width = 6; s.height = 6;
        const sc = s.getContext('2d');
        if (sc) {
          sc.strokeStyle = 'rgba(255,255,255,.6)'; sc.lineWidth = 2;
          sc.beginPath(); sc.moveTo(-1, 7); sc.lineTo(7, -1); sc.stroke();
          shtrih.current = ctx.createPattern(s, 'repeat');
        }
      }
      if (shtrih.current) {
        ctx.globalAlpha = 1; ctx.fillStyle = shtrih.current;
        ctx.beginPath(); kontur(ctx, x1, y, w, h, 4); ctx.fill();
      }
    }
    if (p.krit && !p.zhdet && !raschet) {
      ctx.globalAlpha = 1; ctx.strokeStyle = T.danger; ctx.lineWidth = 2;
      ctx.strokeRect(x1 - 1, y - 2, w + 2, h + 4);
    }
    ctx.globalAlpha = 1;
  }

  /** ломаная «от конца предшественника к началу последователя» со стрелкой */
  function strelka(ctx: CanvasRenderingContext2D, T: Tsveta, x0: number, y0: number, h0: number,
    x1: number, y1: number, vid: string, yarko: boolean, vydelena: boolean) {
    const ot = 7;
    ctx.strokeStyle = vid === 'resurs' ? T.muted : vid === 'ruch' ? T.done : T.accent;
    ctx.fillStyle = ctx.strokeStyle;
    ctx.lineWidth = vydelena ? 1.8 : vid === 'resurs' ? 1 : 1.2;
    ctx.globalAlpha = yarko ? (vydelena ? 1 : 0.75) : 0.18;
    ctx.setLineDash(vid === 'resurs' ? [3, 3] : []);
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    if (x1 >= x0 + ot * 2) {
      ctx.lineTo(x1 - ot, y0); ctx.lineTo(x1 - ot, y1); ctx.lineTo(x1 - 1, y1);
    } else {
      // последователь не правее конца: шаг назад в зазоре между строками
      const shag = y1 > y0 ? y0 + h0 / 2 + 4 : y0 - h0 / 2 - 4;
      ctx.lineTo(x0 + ot, y0); ctx.lineTo(x0 + ot, shag); ctx.lineTo(x1 - ot, shag);
      ctx.lineTo(x1 - ot, y1); ctx.lineTo(x1 - 1, y1);
    }
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x1 - 6, y1 - 3.5); ctx.lineTo(x1 - 6, y1 + 3.5); ctx.closePath(); ctx.fill();
    ctx.globalAlpha = 1;
  }

  function plashka(ctx: CanvasRenderingContext2D, T: Tsveta, s: string, x: number, y: number) {
    ctx.font = '600 11px ' + T.font;
    const w = ctx.measureText(s).width + 12;
    ctx.fillStyle = T.text; ctx.globalAlpha = 0.88;
    ctx.beginPath(); kontur(ctx, x, y - 9, w, 18, 5); ctx.fill();
    ctx.globalAlpha = 1; ctx.fillStyle = '#fff'; ctx.textBaseline = 'middle';
    ctx.fillText(s, x + 6, y + 0.5);
  }

  /** что под мышью: строка и полоса Ганта, край ли это */
  function podMyshyu(e: MouseEvent) {
    const el = box.current, g = geo.current;
    if (!el || !g) return null;
    zapomnitProkrutku();
    const br = el.getBoundingClientRect();
    const cx = e.clientX - br.left - el.clientLeft - g.fixR, cy = e.clientY - br.top - el.clientTop;
    const gx0 = Math.max(0, g.ganttX - skr.current.sl - g.fixR);
    const gx1 = Math.min(g.W, g.ganttX + (P.current.d1 - P.current.d0 + 1) * P.current.px - skr.current.sl - g.fixR);
    if (cx < gx0 || cx >= gx1 || cy < 0 || cy >= g.H) return null;
    if (cy < g.theadH) return { cx, cy, shapka: true, di: -1, bar: null, kray: false };
    const di = Math.floor((cy + skr.current.st - g.y0) / g.pitch) + g.di0;
    const q = geom(g, di);
    if (!q) return { cx, cy, shapka: false, di, bar: null, kray: false };
    const na = cx >= q.x1 - 2 && cx <= q.x2 + 2 && Math.abs(cy - q.yc) <= Math.max(q.h / 2 + 3, 8);
    const kray = na && q.x2 - cx <= 7 && (q.x2 - q.x1 >= 12 || cx >= (q.x1 + q.x2) / 2);
    return { cx, cy, shapka: false, di, bar: na ? q : null, kray };
  }

  // Замер и кадр после каждой отрисовки модели (прокрутка окна, новые данные, столбцы)
  useLayoutEffect(() => {
    zamerit();
    // масштаб поменялся колесом — держим день под мышью на месте
    const el = box.current, g = geo.current, y = zoomYakor.current;
    if (el && g && y) {
      zoomYakor.current = null;
      el.scrollLeft = Math.max(0, g.ganttX + (y.den - props.d0) * px - g.fixR - y.cx);
    }
    risovat();
  });

  /** середина видимой части Ганта, x холста */
  function seredina(g: Geo) {
    zapomnitProkrutku();
    return (Math.max(0, g.ganttX - skr.current.sl - g.fixR) + g.W) / 2;
  }
  /** Масштаб в пределах: мельче нельзя, чем вся шкала во всю ширину окна Ганта —
   *  иначе справа остаётся пустота, и Гант незачем сжимать (2026-09-18) */
  function ogranichitPx(v: number) {
    const g = geo.current;
    const dney = P.current.d1 - P.current.d0 + 1;
    const vlezet = g && dney > 0 ? (g.W - Math.max(0, g.ganttX - g.fixR)) / dney : PX_MIN;
    return Math.min(PX_MAX, Math.max(PX_MIN, vlezet, v));
  }

  // события — на контейнере таблицы: холст прозрачен для мыши
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const skrytPodskazku = () => { if (podskazka.current) podskazka.current.style.display = 'none'; };
    const pokazatPodskazku = (s: string, cx: number, cy: number) => {
      const d = podskazka.current, g = geo.current;
      if (!d || !g) return;
      d.textContent = s;
      d.style.display = 'block';
      const w = d.offsetWidth;
      const x = Math.min(g.fixR + cx + 14, g.fixR + g.W - w - 6);
      d.style.left = Math.max(g.fixR, x) + 'px';
      d.style.top = (cy + 16 + d.offsetHeight > g.H ? cy - d.offsetHeight - 8 : cy + 16) + 'px';
    };
    const move = (e: MouseEvent) => {
      if (tash.current) return;                       // тянем — ведёт документ
      const m = podMyshyu(e);
      const bylo = navod.current;
      navod.current = m && m.bar ? { di: m.di, kray: m.kray } : null;
      const mozhno = P.current.tyanem && !P.current.raschet;
      const kursor = m && m.bar && mozhno ? (m.kray ? 'col-resize' : 'grab') : '';
      if (el.style.cursor !== kursor) el.style.cursor = kursor;
      if (m && m.bar) pokazatPodskazku(m.bar.p.podpis
        + (mozhno ? (m.bar.p.vid === 'zad'
          ? '\nТяните — ручное начало, за правый край — ручная длительность; двойной клик — карточка'
            + (P.current.onSvyaz ? '; вверх или вниз на другую полосу — связь' : '')
          : '\nТяните — группа начнётся не раньше новой даты, за правый край — длительности; '
            + 'двойной клик — сбросить корректировки') : ''), m.cx, m.cy);
      else skrytPodskazku();
      if ((bylo?.di ?? -1) !== (navod.current?.di ?? -1) || bylo?.kray !== navod.current?.kray) zaprositKadr();
    };
    const leave = () => {
      if (tash.current) return;
      if (navod.current) { navod.current = null; zaprositKadr(); }
      el.style.cursor = '';
      skrytPodskazku();
    };
    const down = (e: MouseEvent) => {
      if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey) return;   // Ctrl/Shift — выделение строк
      if (!P.current.tyanem || P.current.raschet) return;
      const m = podMyshyu(e);
      if (!m || !m.bar) return;
      e.preventDefault();                              // без выделения текста
      tash.current = { di: m.di, kray: m.kray, x0: e.clientX, y0: e.clientY, a: m.bar.a, b: m.bar.b, dney: 0, idet: false };
      document.addEventListener('mousemove', tyanut);
      document.addEventListener('mouseup', otpustit);
      document.addEventListener('keydown', otmena);
    };
    const tyanut = (e: MouseEvent) => {
      const t = tash.current;
      if (!t) return;
      const dx = e.clientX - t.x0, dy = e.clientY - t.y0;
      if (!t.idet && Math.abs(dx) < POROG && Math.abs(dy) < POROG) return;
      if (!t.idet) {
        // вертикально — связь (если экран её принимает), горизонтально — сдвиг дат, как было
        t.svyaz = !!P.current.onSvyaz && !t.kray && Math.abs(dy) > Math.abs(dx);
        if (!t.svyaz && Math.abs(dx) < POROG) return;
        t.idet = true;
        el.style.cursor = t.svyaz ? 'crosshair' : t.kray ? 'col-resize' : 'grabbing';
        skrytPodskazku();
      }
      if (t.svyaz) {
        const m = podMyshyu(e);
        t.mx = m?.cx; t.my = m?.cy;
        t.diDo = m && m.bar && m.di !== t.di ? m.di : undefined;
        zaprositKadr();
        return;
      }
      const dney = Math.round(dx / P.current.px);
      t.dney = t.kray ? Math.max(t.a - t.b, dney) : dney;     // короче одного дня не сжать
      zaprositKadr();
    };
    const snyat = () => {
      document.removeEventListener('mousemove', tyanut);
      document.removeEventListener('mouseup', otpustit);
      document.removeEventListener('keydown', otmena);
    };
    const otpustit = () => {
      const t = tash.current;
      snyat();
      tash.current = null;
      el.style.cursor = '';
      if (t && t.idet && t.svyaz) { if (t.diDo != null) P.current.onSvyaz?.(t.di, t.diDo); }
      else if (t && t.idet && t.dney) P.current.onTyanul(t.di, t.kray, t.dney, t.a, t.b);
      zaprositKadr();
    };
    const otmena = (e: KeyboardEvent) => {             // Esc — бросить перетаскивание
      if (e.key !== 'Escape') return;
      snyat();
      tash.current = null;
      el.style.cursor = '';
      zaprositKadr();
    };
    const dvojnoj = (e: MouseEvent) => {
      const m = podMyshyu(e);
      if (!m || !m.bar) return;
      e.preventDefault();
      P.current.onDvojnoj(m.di);
    };
    const koleso = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;            // обычное колесо — прокрутка
      const g = geo.current;
      if (!g) return;
      // Ctrl+колесо над любой частью таблицы — масштаб Ганта, а не страницы: раньше
      // вне полос (над столбцами, правее конца шкалы) браузер увеличивал всю страницу,
      // а после отдаления Гант сужался и мышь оказывалась как раз «вне» (2026-09-18)
      e.preventDefault();
      const nov = ogranichitPx(P.current.px * Math.exp(-e.deltaY * 0.0015));
      if (nov === P.current.px) return;
      const m = podMyshyu(e);
      const cx = m ? m.cx : seredina(g);               // мышь вне Ганта — держим середину
      zoomYakor.current = { den: denX(g, cx), cx };
      P.current.onPx(nov);
    };
    const prokrutka = () => zaprositKadr();
    el.addEventListener('mousemove', move);
    el.addEventListener('mouseleave', leave);
    el.addEventListener('mousedown', down);
    el.addEventListener('dblclick', dvojnoj);
    el.addEventListener('wheel', koleso, { passive: false });
    el.addEventListener('scroll', prokrutka, { passive: true });
    const ro = new ResizeObserver(() => { zamerit(); zaprositKadr(); });
    ro.observe(el);
    return () => {
      el.removeEventListener('mousemove', move);
      el.removeEventListener('mouseleave', leave);
      el.removeEventListener('mousedown', down);
      el.removeEventListener('dblclick', dvojnoj);
      el.removeEventListener('wheel', koleso);
      el.removeEventListener('scroll', prokrutka);
      ro.disconnect();
      snyat();
      el.style.cursor = '';
      if (kadr.current) cancelAnimationFrame(kadr.current);
      kadr.current = 0;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [box]);

  /** кнопки масштаба: шаг — в полтора раза, центр видимой части стоит на месте */
  const masshtab = (k: number) => {
    const el = box.current, g = geo.current;
    if (!el || !g) return;
    const nov = ogranichitPx(px * k);
    if (nov === px) return;
    const cx = seredina(g);
    zoomYakor.current = { den: denX(g, cx), cx };
    onPx(nov);
  };
  const kSegodnya = () => {
    const el = box.current, g = geo.current;
    if (!el || !g) return;
    const d = Math.floor(Date.now() / D1);
    el.scrollLeft = Math.max(0, g.ganttX + (d - props.d0) * px - g.fixR - g.W / 3);
  };
  const pereklSvyazi = () => {
    const v = !vseSvyazi;
    setVseSvyazi(v);
    try { localStorage.setItem('plot_gant_svyazi', v ? '1' : '0'); } catch { /* приватный режим */ }
  };
  useEffect(() => { zaprositKadr(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ },
    [vseSvyazi, props.vybrana]);   // выбрали задачу кликом — сразу показать её связи (#99)
  useEffect(() => {
    props.knopkiVne?.({ vseSvyazi, pereklSvyazi, kSegodnya, masshtab });
    return () => props.knopkiVne?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vseSvyazi, px, !!props.knopkiVne]);

  return (
    <div className="gh-sloj">
      <canvas ref={holst} className="gh-holst" />
      {!props.knopkiVne && <div ref={knopki} className="gh-knopki" style={{ height: razmer.gh }}>
        <span className="gh-knopki-fon">
        <span className="gh-beta" title="Прототип: Гант на холсте. Вернуть прежний — кнопка «Новый Гант» в шапке модели">
          β</span>
        <button className={'gh-kn' + (vseSvyazi ? ' on' : '')} onClick={pereklSvyazi}
          title={vseSvyazi ? 'Показывать связи только у полосы под мышью'
            : 'Показать все связи: сплошные — по справочнику, зелёные — ручные предшественники, '
              + 'пунктир — цепочка бригады. Без этого стрелки видны у полосы под мышью'}>Связи</button>
        <button className="gh-kn" onClick={kSegodnya} title="Прокрутить к сегодняшнему дню">Сегодня</button>
        <button className="gh-kn" onClick={() => masshtab(1 / 1.5)} title="Мельче (Ctrl + колесо над Гантом)">−</button>
        <button className="gh-kn" onClick={() => masshtab(1.5)} title="Крупнее (Ctrl + колесо над Гантом)">+</button>
        </span>
      </div>}
      <div ref={podskazka} className="gh-podskazka" />
    </div>
  );
}
